import { memo, useCallback, useEffect, useMemo, useState } from "react";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";

import { messagesForPatient } from "../aggregations";
import { useSetSelection } from "../hooks/useSetSelection";
import { useTablePagination } from "../hooks/useTablePagination";
import CommunicationsTablePagination from "./CommunicationsTablePagination";
import {
  formatLastAppointmentDisplay,
  formatPatientBirthDateDisplay,
  formatPatientPhoneDisplay,
} from "../helpers/formatCrmCommunicationsDisplay";
import { formatPatientListLabel } from "../patientLabels";
import type { CrmPatientSummary, FluxZapCommunicationRecord } from "../types";
import PatientNameCell from "./PatientNameCell";
import type { OnOpenCommunicationsWhatsAppSend } from "../types/sendMessage";
import CommunicationPatientSelectionBar from "./CommunicationPatientSelectionBar";
import { PatientCommunicationsDetailDialog } from "./CommunicationsDetailDialogs";

export interface CommunicationsShortcutPatientListBlockProps {
  shortcutPatients: CrmPatientSummary[];
  /** Exibe coluna preenchida após busca com filtro de consultas ativo. */
  showLastAppointmentColumn?: boolean;
  /** Histórico FluxZap para o diálogo &quot;Ver detalhes&quot;. */
  communications?: FluxZapCommunicationRecord[];
  onOpenSendMessage: OnOpenCommunicationsWhatsAppSend;
}

/**
 * Lista de pacientes: seleção, WhatsApp real e detalhes (paridade com foco em pacientes).
 */
const CommunicationsShortcutPatientListBlock = memo(
  ({
    shortcutPatients,
    showLastAppointmentColumn = false,
    communications = [],
    onOpenSendMessage,
  }: CommunicationsShortcutPatientListBlockProps) => {
    const {
      selectedIds,
      selectedCount,
      toggle,
      toggleAll,
      clear,
      has,
    } = useSetSelection();

    const [detailPatient, setDetailPatient] = useState<CrmPatientSummary | null>(
      null,
    );

    const allPatientIds = useMemo(
      () => shortcutPatients.map((p) => p.patientId),
      [shortcutPatients],
    );

    const shortcutListResetKey = useMemo(
      () =>
        shortcutPatients.length > 0
          ? `${shortcutPatients.length}-${shortcutPatients[0]?.patientId ?? ""}`
          : "empty",
      [shortcutPatients],
    );

    const {
      paginatedItems: paginatedPatients,
      page,
      rowsPerPage,
      totalCount: shortcutPatientCount,
      handlePageChange,
      handleRowsPerPageChange,
    } = useTablePagination(shortcutPatients, shortcutListResetKey);

    const allSelected =
      allPatientIds.length > 0 &&
      allPatientIds.every((id) => selectedIds.has(id));
    const someSelected = allPatientIds.some((id) => selectedIds.has(id));

    const detailMessages = useMemo(() => {
      if (!detailPatient) return [];
      return messagesForPatient(communications, detailPatient.patientId);
    }, [communications, detailPatient]);

    const emptyColSpan = showLastAppointmentColumn ? 6 : 5;

    useEffect(() => {
      clear();
    }, [shortcutPatients, clear]);

    const closeDetail = useCallback(() => setDetailPatient(null), []);

    const openSendForIds = useCallback(
      (ids: Iterable<string>) => {
        const patients: CrmPatientSummary[] = [];
        for (const id of ids) {
          const p = shortcutPatients.find((x) => x.patientId === id);
          if (p) patients.push(p);
        }
        if (patients.length > 0) {
          onOpenSendMessage({ patients });
        }
      },
      [onOpenSendMessage, shortcutPatients],
    );

    return (
      <Stack spacing={1.5}>
        <CommunicationPatientSelectionBar
          selectedCount={selectedCount}
          onSendMessage={() => openSendForIds(selectedIds)}
          onClearSelection={clear}
        />

        <TableContainer component={Paper} variant="outlined">
          <Table size="small" aria-label="Resultado do atalho selecionado">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={someSelected && !allSelected}
                    onChange={() => toggleAll(allPatientIds)}
                    inputProps={{
                      "aria-label":
                        "Selecionar todos os pacientes desta lista",
                    }}
                  />
                </TableCell>
                <TableCell>Paciente</TableCell>
                <TableCell>Contato</TableCell>
                <TableCell>Nascimento</TableCell>
                {showLastAppointmentColumn ? (
                  <TableCell>Última consulta</TableCell>
                ) : null}
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shortcutPatientCount === 0 ? (
                <TableRow>
                  <TableCell colSpan={emptyColSpan}>
                    <Box sx={{ py: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Nenhum paciente encontrado com estes critérios.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedPatients.map((p) => {
                  const checked = has(p.patientId);
                  return (
                    <TableRow key={p.patientId} hover selected={checked}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={checked}
                          onChange={() => toggle(p.patientId)}
                          inputProps={{
                            "aria-label": `Selecionar ${formatPatientListLabel(p)}`,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <PatientNameCell patient={p} />
                      </TableCell>
                      <TableCell>{formatPatientPhoneDisplay(p.phone)}</TableCell>
                      <TableCell>
                        {formatPatientBirthDateDisplay(p.birthDate)}
                      </TableCell>
                      {showLastAppointmentColumn ? (
                        <TableCell>
                          {formatLastAppointmentDisplay(p.lastAppointmentAt)}
                        </TableCell>
                      ) : null}
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setDetailPatient(p)}
                          aria-label={`Ver detalhes de ${formatPatientListLabel(p)}`}
                        >
                          Ver detalhes
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <CommunicationsTablePagination
          count={shortcutPatientCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />

        <PatientCommunicationsDetailDialog
          open={detailPatient != null}
          onClose={closeDetail}
          patient={detailPatient}
          messages={detailMessages}
        />
      </Stack>
    );
  },
);

CommunicationsShortcutPatientListBlock.displayName =
  "CommunicationsShortcutPatientListBlock";

export default CommunicationsShortcutPatientListBlock;
